import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import api, { BACKEND_URL, formatApiError } from "../lib/api";
import { addToWatchlist, removeFromWatchlist, getWatchlist } from "../lib/watchlist";
import { ZoneChip, LageBadge, Stat, Button, MockBadge, Spinner } from "../components/ui";
import { ArrowLeft, ExternalLink, Bookmark, Sparkles, BookmarkCheck, FileDown, FileText, Upload } from "lucide-react";
import ComparablesCarousel from "../components/ComparablesCarousel";
import { useI18n } from "../i18n/I18nContext";
import { isLowConfidence } from "../lib/mvConfidence";
import { getObjectCockpitDefaults } from "../lib/objectCockpit";
import { getMarketStateName } from "../lib/marketMeta";

const DOC_TYPES = [
  "Energieausweis",
  "Grundbuch",
  "Mietvertrag",
  "Nutzwertgutachten",
  "Objektabrechnungen",
  "Bestandspläne",
  "ETV-Protokolle",
  "Monatsvorschreibungen",
  "Wohnungseigentumsvertrag",
];

// Shared prefill payload for the calculators (Objekt-Cockpit + Finanzierungsdossier).
// The dossier consumes the same shape as `cockpit` nav state; the cockpit reads it
// as `prefill` nav state. `listingUrl`/`backTo` power the back-to-listing chrome.
function buildCockpitPrefill(l) {
  const marketCode = l.market_code || l.market || "";
  const inferredState = getMarketStateName(marketCode, l.market_name || l.market);
  const isAustrian = l.country === "AT" || String(marketCode).startsWith("WIEN");
  return {
    listingId: l.id,
    form: {
      ...getObjectCockpitDefaults(),
      title: l.title || "Finanzierungsdossier",
      address: l.address || "",
      country: isAustrian ? "AT" : "DE",
      state: l.state || inferredState || "",
      purchasePrice: l.price || 0,
      livingAreaSqm: l.size_m2 || 0,
      coldRentPerSqmMonthly: l.net_rent_per_sqm || 0,
      ltv: l.ltv ?? 0.8,
      rate: l.rate ?? 0.04,
    },
    rentBasis: l.rent_source === "actual" ? "actual_contract" : "market_reference",
    rentSourceNote: l.rent_source === "actual" ? "Inserat: bestehende Miete, vor Freigabe gegen Mietvertrag prüfen" : "immo-watch Markt-/Kalibrierungsreferenz",
    inputOrigin: "listing",
    listingUrl: l.url || "",
    backTo: "/listings/" + l.id,
  };
}

export default function ListingDetail() {
  const { t } = useI18n();
  const { id } = useParams();
  const nav = useNavigate();
  const { openChat } = useOutletContext() || {};
  const [l, setL] = useState(null);
  const [saved, setSaved] = useState(false);
  const [note, setNote] = useState("");
  const [noteExpiresAt, setNoteExpiresAt] = useState(null);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState("");
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [docUploading, setDocUploading] = useState(false);
  const [docResult, setDocResult] = useState(null);
  const [docError, setDocError] = useState("");
  const [documents, setDocuments] = useState([]);

  const refreshWatchState = async () => {
    try {
      const watchlist = await getWatchlist();
      const w = watchlist.find((x) => x.id === id);
      setSaved(!!w);
      setNote(w?.note || "");
      setNoteExpiresAt(w?.note_expires_at || null);
    } catch { /* watchlist state is supplementary — don't block the listing view */ }
  };

  const refreshDocuments = async () => {
    try {
      const { data } = await api.get("/analyze/documents", { params: { listing_id: id } });
      setDocuments(data.documents || []);
    } catch {
      setDocuments([]);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const { data } = await api.get(`/listings/${id}`, { signal: controller.signal });
        setL(data.listing);
      } catch (e) {
        if (axios.isCancel(e)) return;
        console.error("Listing fetch failed", e);
        setL({ error: true });
      }
      if (!controller.signal.aborted) await refreshWatchState();
      if (!controller.signal.aborted) await refreshDocuments();
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggleSave = async () => {
    if (saved) {
      await removeFromWatchlist(id);
    } else {
      await addToWatchlist(l);
    }
    await refreshWatchState();
  };

  const saveNote = async () => {
    setNoteSaving(true);
    setNoteError("");
    try {
      await api.put(`/watchlist/${id}/note`, { note });
      await refreshWatchState();
    } catch (e) {
      setNoteError(formatApiError(e));
    } finally {
      setNoteSaving(false);
    }
  };

  const uploadObjectDoc = async (file) => {
    if (!file) return;
    setDocUploading(true);
    setDocResult(null);
    setDocError("");
    try {
      if (file.size > 16 * 1024 * 1024) {
        throw new Error("Datei > 16 MB — bitte verkleinern");
      }
      const fd = new FormData();
      fd.append("document_type", docType);
      fd.append("listing_id", id);
      fd.append("file", file);
      const { data } = await api.post("/analyze/documents/upload", fd);
      setDocResult(data);
      await refreshDocuments();
    } catch (e) {
      setDocError(e?.response?.data?.detail || e.message || "Dokument-Upload fehlgeschlagen");
    } finally {
      setDocUploading(false);
    }
  };

  if (!l) return <div className="p-10"><Spinner /></div>;
  if (l.error) return <div className="p-10 text-signal-red font-mono text-sm">{t("listingDetail.failedLoad")}</div>;

  // MRG-Vollanwendung confidence tier (invest_config.mrg_regime): confirmed
  // pre-1953 uses mrg_applies (red banner below); this is the post-1953
  // cohort where Förderungsstatus can't be checked from listing data, so
  // Vollanwendung is neither confirmed nor ruled out — softer (yellow), not
  // silent. Mutually exclusive with mrg_applies by construction (screen.py);
  // the wgg_flag guard keeps it from firing alongside the WGG banner, since
  // WGG isn't MRG-Richtwert-governed at all.
  const mrgUnsure = l.mrg_regime === "UNKNOWN" && l.mrg_confidence === "UNCERTAIN" && !l.wgg_flag;
  const districtContext = l.district_context;
  const listingDocuments = documents.filter((doc) => !doc.deal_id);
  const dealDocuments = documents.filter((doc) => doc.deal_id);

  return (
    <div className="reveal">
      {/* HEADER */}
      <div className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 opacity-40">
          <img src={l.image_url} className="w-full h-full object-cover" alt="" />
          <div className="absolute inset-0 bg-gradient-to-r from-bg via-bg/85 to-bg/20" />
        </div>
        <div className="relative px-6 md:px-10 py-8">
          <button
            onClick={() => nav(-1)}
            className="overline hover:text-white inline-flex items-center gap-2"
          >
            <ArrowLeft size={12} /> {t("listingDetail.back")}
          </button>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <ZoneChip zone={l.zone} size="lg" />
            <LageBadge grade={l.lage_grade} />
            {l.is_fast_deal && (
              <span title={t("listingDetail.fastDealTooltip")} className="text-[10px] font-mono text-orange-400 border border-orange-400/50 px-2 py-0.5">
                {l.is_new ? t("listingDetail.fastNew") : t("listingDetail.fastDeal")}
              </span>
            )}
            <span className="overline">{l.platform}</span>
            <span className="overline text-text-muted">· {l.id}</span>
            <MockBadge className="ml-2" />
          </div>
          <h1 className="mt-3 font-heading text-3xl md:text-5xl font-black tracking-tight">{l.title}</h1>
          <div className="mt-2 text-text-secondary text-sm">{l.address}</div>
          <div className="mt-6 flex flex-wrap gap-2">
            <Button onClick={() => openChat(l)} variant="green" data-testid="listing-ai-btn">
              <Sparkles size={14} /> {t("listingDetail.askAiBtn")}
            </Button>
            <Button onClick={toggleSave} variant={saved ? "ghost" : "ghost"} data-testid="listing-save-btn">
              {saved ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
              {saved ? t("listingDetail.saved") : t("listingDetail.saveToWatchlist")}
            </Button>
            <button
              onClick={async () => {
                const res = await api.get(`/export/listing/${l.id}.md`, { responseType: "blob" });
                const blob = new Blob([res.data], { type: "text/markdown" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                const slug = (l.market_name || "listing").toLowerCase().replace(/[\s/]+/g, "-").replace(/[^a-z0-9-]/g, "");
                a.href = url; a.download = `${slug}-f${l.faktor}.md`; a.click();
                URL.revokeObjectURL(url);
              }}
              data-testid="listing-export-md"
              className="inline-flex items-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-widest2 border border-zinc-700 text-text-secondary hover:border-white hover:text-white"
            >
              <FileDown size={14} /> {t("listingDetail.exportMd")}
            </button>
            <a
              href={l.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-widest2 border border-zinc-700 text-text-secondary hover:border-white hover:text-white"
            >
              <ExternalLink size={14} /> {t("listingDetail.source")}
            </a>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 border-b border-border" data-testid="listing-kpis">
        <Stat label={t("dashboard.colFaktor")} value={(l.faktor ?? 0).toFixed(1)} sub={t("listingDetail.lageThreshold", { grade: l.lage_grade })} accent={l.zone.includes("GREEN") ? "green" : l.zone === "YELLOW" ? "yellow" : "red"} />
        <Stat label="KAUFPREIS" value={`€${(l.price/1000).toFixed(0)}k`} sub={`€${(l.price_per_sqm || 0).toLocaleString()}/m²`} />
        <Stat
          label={t("listingDetail.netRent")}
          value={l.net_rent_per_sqm != null ? `€${l.net_rent_per_sqm.toFixed(2)}/m²` : "—"}
          sub={
            l.mrg_applies === true ? t("listingDetail.netRentMrgCapped")
            : l.wgg_flag === true ? t("listingDetail.netRentWgg")
            : mrgUnsure ? t("listingDetail.netRentMrgUnsure")
            : l.annual_net_rent ? `€${l.annual_net_rent.toLocaleString()}/yr · ${l.rent_source === "actual" ? t("listingDetail.stated") : t("listingDetail.calibrated")}`
            : t("listingDetail.noRentData")
          }
          accent={(l.mrg_applies === true || l.wgg_flag === true) ? "red" : mrgUnsure ? "yellow" : "white"}
        />
        <Stat label={t("listings.vsMarkt")} value={l.vs_markt_pct != null ? `${l.vs_markt_pct > 0 ? "+" : ""}${l.vs_markt_pct}%` : "—"} sub={l.mv_spread != null ? t("listingDetail.mvSpreadSub", { band: l.mv_band_used || t("listingDetail.vsMarketMedian"), spread: l.mv_spread.toFixed(2) }) : t("listingDetail.vsMarketMedian")} accent={isLowConfidence(l) ? undefined : (l.vs_markt_pct ?? 0) < 0 ? "green" : "red"} />
        <Stat label={t("dashboard.colCashflow")} value={`${(l.monthly_cashflow ?? 0) >= 0 ? "+" : ""}€${l.monthly_cashflow ?? "—"}`} sub={t("listingDetail.cashflowSub")} accent={(l.monthly_cashflow ?? 0) >= 0 ? "green" : "red"} />
      </div>

      {/* LEGAL CHECK — MRG-Vollanwendung / WGG: the net rent above is free-market and
          may not be legally/practically achievable. Loud on purpose — this is exactly
          the number a buy decision would otherwise silently rest on. */}
      {(l.mrg_applies === true || l.wgg_flag === true) && (
        <div className="border-b border-signal-red/40 bg-signal-red/5 px-6 md:px-10 py-4">
          <div className="flex items-start gap-3">
            <span className="text-signal-red font-mono text-sm leading-none mt-0.5">⚠</span>
            <div>
              <div className="overline text-signal-red">{t("listingDetail.legalCheckTitle")}</div>
              {l.mrg_applies === true && (
                <p className="mt-1 text-sm text-text-secondary leading-relaxed">
                  {t("listingDetail.mrgWarning", {
                    baujahr: l.baujahr ?? "?",
                    floorRent: l.floor_rent_m2 != null ? l.floor_rent_m2.toFixed(2) : "?",
                    floorFaktor: l.floor_faktor ?? "?",
                    floorCf: l.floor_cf_mo != null ? Math.round(l.floor_cf_mo) : "?",
                  })}
                </p>
              )}
              {l.wgg_flag === true && (
                <p className="mt-1 text-sm text-text-secondary leading-relaxed">
                  {t("listingDetail.wggWarning")}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      {mrgUnsure && (
        <div className="border-b border-signal-yellow/40 bg-signal-yellow/10 px-6 md:px-10 py-4">
          <div className="flex items-start gap-3">
            <span className="text-signal-yellow font-mono text-sm leading-none mt-0.5">⚠</span>
            <div>
              <div className="overline text-signal-yellow">{t("listingDetail.legalCheckTitle")}</div>
              <p className="mt-1 text-sm text-text-secondary leading-relaxed">
                {t("listingDetail.mrgUnsureWarning", {
                  baujahr: l.baujahr ?? "?",
                  floorRent: l.floor_rent_m2 != null ? l.floor_rent_m2.toFixed(2) : "?",
                  floorFaktor: l.floor_faktor ?? "?",
                  floorCf: l.floor_cf_mo != null ? Math.round(l.floor_cf_mo) : "?",
                })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* MRG scenario panel — quantifies what the banners above warn about */}
      {l.mrg_analysis && !l.wgg_flag && <MrgAnalysisPanel analysis={l.mrg_analysis} t={t} />}

      {/* BODY */}
      <div className="grid grid-cols-12">
        {/* LEFT: details */}
        <section className="col-span-12 lg:col-span-8 border-r border-border p-6 md:p-8 space-y-8">
          <div>
            <div className="overline">{t("listingDetail.propertyFacts")}</div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
              <Fact label={t("listingDetail.factSize")} value={`${l.size_m2} m²`} />
              <Fact label={t("listingDetail.factRooms")} value={l.rooms} />
              <Fact label={t("listingDetail.factMarket")} value={l.market_name} />
              <Fact label={t("listingDetail.factLageGrade")} value={l.lage_grade} />
              <Fact label={t("listingDetail.factBaujahr")} value={l.baujahr ?? "—"} />
              <Fact label={t("listingDetail.factMrgStatus")} value={
                l.mrg_applies === true ? t("listingDetail.mrgStatusVollanwendung")
                : l.wgg_flag === true ? t("listingDetail.mrgStatusWgg")
                : mrgUnsure ? t("listingDetail.mrgStatusUnsure")
                : "—"
              } />
              <Fact label={t("listingDetail.factDaysOnMarket")} value={l.days_on_market != null ? `${l.days_on_market}d` : "—"} />
              <Fact label={t("listingDetail.factConditionScore")} value={l.condition_score} />
              <Fact label={t("listingDetail.factPosted")} value={l.posted_at ? new Date(l.posted_at).toLocaleDateString("en-GB") : "—"} />
            </div>
          </div>

          <div>
            <div className="overline">{t("listingDetail.conditionFlags")}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {(l.condition_tags || l.flags || []).map((tag) => (
                <span key={tag} className="border border-border bg-surface px-2.5 py-1 text-[11px] font-mono text-text-secondary">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {l.flip_verdict && (
            <div>
              <div className="overline">{t("listingDetail.flipScan")}</div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
                <Fact
                  label={t("listingDetail.flipVerdictLabel")}
                  value={t(`fixflip.verdict${{ buy: "Buy", negotiate: "Negotiate", reject: "Reject" }[l.flip_verdict] ?? "Reject"}`)}
                />
                <Fact label={t("listingDetail.flipMax")} value={l.flip_max_ankauf != null ? `€${Math.round(l.flip_max_ankauf / 1000)}k` : "—"} />
                <Fact label={t("listingDetail.flipVkEst")} value={l.flip_vk_est != null ? `€${Math.round(l.flip_vk_est / 1000)}k` : "—"} />
                <Fact label={t("listingDetail.flipDiff")} value={l.flip_diff_pct != null ? `${l.flip_diff_pct > 0 ? "+" : ""}${l.flip_diff_pct}%` : "—"} />
              </div>
              <p className="mt-2 text-[10px] font-mono text-text-muted">{t("listingDetail.flipScanNote")}</p>
            </div>
          )}

          <div>
            <div className="overline">{t("listingDetail.underwritingBase")}</div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-px bg-border border border-border">
              <Fact label={t("listingDetail.factLtv")} value={`${(l.ltv * 100).toFixed(0)}%`} />
              <Fact label={t("listingDetail.factRate")} value={`${(l.rate * 100).toFixed(2)}%`} />
              <Fact label={t("listingDetail.factMortgageMo")} value={`€${l.mortgage_monthly}`} />
              <Fact label={t("listingDetail.factOpexMo")} value={l.opex_monthly != null ? `€${l.opex_monthly}` : "—"} />
              <Fact label={t("listingDetail.factNetRentMo")} value={`€${Math.round((l.annual_net_rent || 0) / 12)}`} />
              <Fact label={t("listingDetail.factGrossYield")} value={l.price ? `${(l.annual_net_rent / l.price * 100).toFixed(2)}%` : "—"} />
              <Fact label={t("listingDetail.factLoan")} value={l.price ? `€${Math.round(l.price * l.ltv / 1000)}k` : "—"} />
              <Fact label={t("listingDetail.factEquityIn")} value={l.price ? `€${Math.round(l.price * (1 - l.ltv) / 1000)}k` : "—"} />
              <Fact label={t("listingDetail.factCashOnCash")} value={
                l.monthly_cashflow != null && l.price && l.ltv < 1
                  ? `${((l.monthly_cashflow * 12) / (l.price * (1 - l.ltv)) * 100).toFixed(1)}%`
                  : "—"
              } />
              <Fact label={t("listingDetail.factRentCover")} value={
                l.mortgage_monthly > 0 && l.annual_net_rent
                  ? `${Math.round((l.annual_net_rent / 12) / l.mortgage_monthly * 100)}%`
                  : "—"
              } />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                onClick={() => nav("/objekt-cockpit", { state: { prefill: buildCockpitPrefill(l) } })}
                variant="ghost"
                data-testid="open-objekt-cockpit"
              >
                {t("listingDetail.openUnderwriter")}
              </Button>
              <Button
                onClick={() => nav("/finanzierungsdossier", { state: { cockpit: buildCockpitPrefill(l) } })}
                variant="green"
                data-testid="open-financing-dossier"
              >
                <FileText size={13} /> Finanzierungsdossier
              </Button>
              <Button onClick={() => nav(`/bewerten?id=${l.id}`)} variant="ghost" data-testid="open-bewerter">
                {t("listingDetail.openBewerter")}
              </Button>
              <Button onClick={() => nav(`/fixflip?id=${l.id}`)} variant="ghost" data-testid="open-fixflip">
                {t("listingDetail.openFixflip")}
              </Button>
            </div>
          </div>
          <div>
            <div className="overline">{t("listingDetail.comparableDeals")}</div>
            <div className="mt-3">
              <ComparablesCarousel listingId={l.id} />
            </div>
          </div>
        </section>

        {/* RIGHT: market position bar */}
        <aside className="col-span-12 lg:col-span-4 p-6 md:p-8 space-y-6">
          <div>
            <div className="overline">{t("listingDetail.zonePosition")}</div>
            <FaktorBar faktor={l.faktor} grade={l.lage_grade} t={t} />
          </div>

          <div className="border border-border p-4">
            <div className="overline">{t("listingDetail.faktorRationale")}</div>
            <p className="mt-2 text-sm text-text-secondary leading-relaxed">
              {l.zone === "GREEN" && t("listingDetail.rationaleGreen", { rate: "4.5%" })}
              {l.zone === "GREEN_BORDER" && t("listingDetail.rationaleGreenBorder")}
              {l.zone === "YELLOW" && t("listingDetail.rationaleYellow")}
              {l.zone === "RED" && t("listingDetail.rationaleRed", { rate: "4.5%" })}
            </p>
          </div>

          {districtContext && (
            <div className="border border-border p-4" data-testid="district-context">
              <div className="overline">{t(`listingDetail.${districtContext.mode_key || "districtReferenceTitle"}`)}</div>
              <p className="mt-2 text-xs text-text-secondary leading-relaxed">
                {t("listingDetail.districtContextSub")}
              </p>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {districtContext.comparison?.diff_pct != null ? (
                  <>
                    <span className={`border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest2 ${
                      districtContext.comparison.accent === "green"
                        ? "border-signal-green/40 text-signal-green"
                        : districtContext.comparison.accent === "red"
                          ? "border-signal-red/40 text-signal-red"
                          : "border-signal-yellow/40 text-signal-yellow"
                    }`}>
                      {t(`listingDetail.${districtContext.comparison.label_key}`)}
                    </span>
                    <span className="text-[11px] font-mono text-text-muted">
                      {t("listingDetail.districtContextCompared", { pct: Math.abs(districtContext.comparison.diff_pct).toFixed(1) })}
                    </span>
                  </>
                ) : (
                  <span className="text-[11px] font-mono text-text-muted">
                    {t(`listingDetail.${districtContext.comparison?.label_key || "districtContextNoPrior"}`)}
                  </span>
                )}
              </div>

              {districtContext.prior && (
                <div className="mt-3 grid grid-cols-2 gap-px bg-border border border-border">
                  <div className="bg-bg p-3">
                    <div className="overline">{t("listingDetail.districtContextPriorSqm")}</div>
                    <div className="num text-sm mt-1 text-white">
                      €{Number(districtContext.prior.eur_sqm).toFixed(2)}/m²
                    </div>
                  </div>
                  <div className="bg-bg p-3">
                    <div className="overline">{t("listingDetail.districtContextPriorMo")}</div>
                    <div className="num text-sm mt-1 text-white">
                      {districtContext.prior.monthly != null
                        ? `€${Number(districtContext.prior.monthly).toLocaleString()}`
                        : "—"}
                    </div>
                  </div>
                </div>
              )}

              {!!districtContext.reference_rows?.length && (
                <div className="mt-3 space-y-2">
                  {districtContext.reference_rows.map((row) => (
                    <div key={`${row.year || "?"}-${row.segment || "?"}-${row.district || "?"}`} className="border-t border-border/60 pt-2 text-[11px] font-mono leading-5">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-text-muted">
                          {row.year || "—"} · {row.segment || "—"}
                        </span>
                        <span className="text-white truncate">{row.district || "—"}</span>
                      </div>
                      <div className="mt-0.5 text-text-secondary">
                        {row.eur_sqm != null ? `€${Number(row.eur_sqm).toFixed(2)}/m²` : "—"}
                        {row.delta_pct != null ? ` · ${row.delta_pct > 0 ? "+" : ""}${row.delta_pct}%` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <p className="mt-3 text-[11px] text-text-muted leading-relaxed">
                {t("listingDetail.districtContextSource", {
                  source: districtContext.source?.name || "willhaben Mietpreisspiegel",
                  warning: districtContext.source?.warning || "Reference only; do not treat as contract rent.",
                })}
              </p>
              {districtContext.uses_prior && (
                <p className="mt-1 text-[11px] text-text-muted leading-relaxed">
                  {t("listingDetail.districtContextFallback")}
                </p>
              )}
            </div>
          )}

          <div className="border border-border p-4" data-testid="listing-doc-upload">
            <div className="overline inline-flex items-center gap-2">
              <Upload size={12} /> {t("listingDetail.objectDocsTitle")}
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3">
              <label className="block">
                <span className="overline block mb-1">{t("listingDetail.objectDocsType")}</span>
                <select
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  className="w-full bg-bg border border-border px-3 py-2 text-sm font-mono text-white focus:border-white outline-none"
                >
                  {DOC_TYPES.map((tpe) => (
                    <option key={tpe} value={tpe}>{tpe}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="overline block mb-1">{t("listingDetail.objectDocsUpload")}</span>
                <input
                  type="file"
                  accept="application/pdf,image/png,image/jpeg,image/webp"
                  disabled={docUploading}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (!file) return;
                    uploadObjectDoc(file);
                  }}
                  className="block w-full text-xs font-mono text-text-secondary file:mr-3 file:border file:border-zinc-700 file:bg-transparent file:px-3 file:py-1.5 file:text-xs file:font-mono file:uppercase file:text-white file:cursor-pointer"
                  data-testid="listing-doc-file"
                />
              </label>
            </div>
            <div className="mt-2 text-[10px] text-text-muted">
              {t("listingDetail.objectDocsHint")}
            </div>
            {docError && <div className="mt-2 text-[11px] font-mono text-signal-red">{docError}</div>}
            {docUploading && <div className="mt-2"><Spinner size={13} /></div>}
            {docResult && (
              <div className="mt-3 space-y-3 border border-border bg-bg p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs font-mono text-signal-green">
                    ✓ {docResult.document?.document_type || docType} {t("listingDetail.objectDocsUploaded")}
                  </div>
                  {docResult.document?.document_id && (
                    <a
                      href={`${BACKEND_URL}/api/export/document/${docResult.document.document_id}.md`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[10px] font-mono text-text-muted hover:text-white"
                      data-testid="listing-doc-export-md"
                    >
                      md
                    </a>
                  )}
                </div>
                {(docResult.warnings || []).map((w, i) => (
                  <div key={i} className="text-[11px] font-mono text-signal-yellow">⚠ {w}</div>
                ))}
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <div className="overline mb-1">{t("listingDetail.objectDocsFields")}</div>
                    <pre className="max-h-64 overflow-auto text-[10px] font-mono whitespace-pre-wrap bg-bg border border-border p-2">
                      {JSON.stringify(docResult.fields || {}, null, 2)}
                    </pre>
                  </div>
                  <div>
                    <div className="overline mb-1">{t("listingDetail.objectDocsVault")}</div>
                    <pre
                      className="max-h-64 overflow-auto text-[10px] font-mono whitespace-pre-wrap bg-bg border border-border p-2"
                      data-testid="listing-doc-vault-copy"
                    >
                      {JSON.stringify(docResult.vault_copy || {}, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            )}
            {documents.length > 0 && (
              <div className="mt-3 space-y-4">
                <div data-testid="listing-docs-listing-group">
                  <div className="overline">{t("listingDetail.objectDocsListingGroup")}</div>
                  {listingDocuments.length > 0 ? (
                    <div className="mt-2 space-y-2">
                      {listingDocuments.map((doc) => (
                        <DocumentRow
                          key={doc.document_id}
                          doc={doc}
                          status={t("listingDetail.objectDocsStatusListing")}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 text-[11px] font-mono text-text-muted">
                      {t("listingDetail.objectDocsNone")}
                    </div>
                  )}
                </div>
                <div data-testid="listing-docs-deal-group">
                  <div className="overline">{t("listingDetail.objectDocsDealGroup")}</div>
                  {dealDocuments.length > 0 ? (
                    <div className="mt-2 space-y-2">
                      {dealDocuments.map((doc) => (
                        <DocumentRow
                          key={doc.document_id}
                          doc={doc}
                          status={t("listingDetail.objectDocsStatusDeal")}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 text-[11px] font-mono text-text-muted">
                      {t("listingDetail.objectDocsNone")}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="border border-border p-4 ai-glow">
            <div className="flex items-center gap-2 text-signal-green">
              <Sparkles size={14} />
              <span className="overline text-signal-green">{t("listingDetail.aiInsight")}</span>
            </div>
            <p className="mt-2 text-xs text-text-secondary leading-relaxed">
              {t("listingDetail.aiInsightBody")}
            </p>
            <Button onClick={() => openChat(l)} variant="green" className="mt-3 w-full">
              <Sparkles size={12} /> {t("listingDetail.askImmoAi")}
            </Button>
          </div>

          {saved && (
            <div className="border border-border p-4">
              <div className="overline">{t("listingDetail.noteForAi")}</div>
              <p className="mt-1 text-[11px] text-text-muted">
                {t("listingDetail.noteHint")}
              </p>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("listingDetail.notePlaceholder")}
                rows={4}
                maxLength={2000}
                className="mt-2 w-full bg-bg border border-border px-3 py-2 text-sm font-mono text-white focus:border-white outline-none resize-none"
              />
              {noteError && <div className="mt-1 text-[10px] font-mono text-signal-red">{noteError}</div>}
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[10px] font-mono text-text-muted">
                  {noteExpiresAt ? t("listingDetail.noteExpires", { date: new Date(noteExpiresAt).toLocaleDateString("en-GB") }) : t("listingDetail.noteNoneYet")}
                </span>
                <Button onClick={saveNote} variant="ghost" disabled={noteSaving}>
                  {noteSaving ? <Spinner size={12} /> : t("listingDetail.saveNote")}
                </Button>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

// MRG scenario panel („Trägt sich der Altbau?") — explains the legal cap the
// banners above warn about: walks the deal through the achievable-rent
// scenarios (Richtwert → Lagepauschale → Top-Lage → möbliert) plus the escape
// paths out of Vollanwendung. Backend field `mrg_analysis` on Kauf listing
// docs; absent on unanalyzed listings → no panel. Suppressed for WGG
// (cost-recovery rent, not Richtwert-governed).
const MRG_SCENARIO_I18N = {
  richtwert: ["mrgAnalysisScenarioRichtwert", "mrgAnalysisHintRichtwert"],
  lage25: ["mrgAnalysisScenarioLage25", "mrgAnalysisHintLage25"],
  lage100: ["mrgAnalysisScenarioLage100", "mrgAnalysisHintLage100"],
  moebliert: ["mrgAnalysisScenarioMoebliert", "mrgAnalysisHintMoebliert"],
  // basis "baujahr_unknown": which rent regime applies is open (Baujahr unknown).
  voll: ["mrgAnalysisScenarioVoll", "mrgAnalysisHintVoll"],
  angemessen: ["mrgAnalysisScenarioAngemessen", "mrgAnalysisHintAngemessen"],
  frei: ["mrgAnalysisScenarioFrei", "mrgAnalysisHintFrei"],
};
const MRG_CHECKLIST_I18N = {
  dachausbau: "mrgAnalysisChecklistDachausbau",
  grundsanierung: "mrgAnalysisChecklistGrundsanierung",
  kategorie_a: "mrgAnalysisChecklistKategorieA",
  lagezuschlag_begruendung: "mrgAnalysisChecklistLagezuschlagBegruendung",
  // Key before Wien's Lagezuschlag went to 0 — still on archived listing docs.
  lagezuschlagskarte: "mrgAnalysisChecklistLagezuschlagskarte",
  baubewilligung: "mrgAnalysisChecklistBaubewilligung",
};
const MRG_VERDICT_CHIP = {
  green: "border-signal-green/40 text-signal-green",
  border: "border-signal-yellow/40 text-signal-yellow",
  no: "border-signal-red/40 text-signal-red",
};
const MRG_VERDICT_I18N = {
  green: "mrgAnalysisVerdictGreen",
  border: "mrgAnalysisVerdictBorder",
  no: "mrgAnalysisVerdictNo",
};
const MRG_GRID = "grid grid-cols-[minmax(0,1fr)_auto_auto_auto_auto] items-center gap-x-4 px-3 py-2";

function MrgAnalysisPanel({ analysis, t }) {
  const scenarios = analysis.scenarios || [];
  const checklist = analysis.checklist || [];
  // Baujahr unknown: the "frei" scenario IS the market rent, so the reference
  // row would only repeat it.
  const baujahrUnknown = analysis.basis === "baujahr_unknown";
  return (
    <div className="border-b border-border px-6 md:px-10 py-5" data-testid="mrg-analysis-panel">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="overline">{t("listingDetail.mrgAnalysisOverline")}</div>
          <h2 className="mt-1 font-heading text-lg md:text-xl font-bold tracking-tight">
            {baujahrUnknown
              ? t("listingDetail.mrgAnalysisTitleBaujahrUnknown")
              : analysis.regime === "VOLLANWENDUNG"
                ? t("listingDetail.mrgAnalysisTitleVoll")
                : t("listingDetail.mrgAnalysisTitleUnknown")}
          </h2>
        </div>
        <div className="text-[10px] font-mono text-text-muted">
          {t("listingDetail.mrgAnalysisSourceSuffix", { as_of: analysis.as_of || "?", bundesland: analysis.bundesland || "?" })}
        </div>
      </div>

      {baujahrUnknown && (
        <div className="mt-2 text-[11px] text-text-secondary leading-relaxed" data-testid="mrg-analysis-unknown-note">
          {analysis.cap_binds === false
            ? t("listingDetail.mrgAnalysisCapNotBinding")
            : analysis.altbau_likely
              ? t("listingDetail.mrgAnalysisAltbauLikely", { reason: analysis.altbau_likely })
              : t("listingDetail.mrgAnalysisAltbauUnlikely")}
        </div>
      )}

      {analysis.size_exempt && (
        <span
          className="mt-2 inline-block border border-signal-green/40 text-signal-green px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest2"
          data-testid="mrg-analysis-size-exempt"
        >
          {t("listingDetail.mrgAnalysisSizeExempt")}
        </span>
      )}

      <div className="mt-4 border border-border">
        <div className={`${MRG_GRID} border-b border-border overline`}>
          <span>{t("listingDetail.mrgAnalysisColScenario")}</span>
          <span>{t("listingDetail.mrgAnalysisColCeiling")}</span>
          <span>{t("listingDetail.mrgAnalysisColFaktor")}</span>
          <span>{t("listingDetail.mrgAnalysisColCf")}</span>
          <span />
        </div>
        {!baujahrUnknown && (
        <div className={`${MRG_GRID} border-b border-border/60 text-text-muted`} data-testid="mrg-scenario-reference">
          <div className="min-w-0">
            <div className="text-sm text-text-secondary">{t("listingDetail.mrgAnalysisRefLabel")}</div>
            <div className="text-[10px] font-mono">{t("listingDetail.mrgAnalysisRefHint")}</div>
          </div>
          <span className="num text-sm">—</span>
          <span className="num text-sm">{analysis.market_faktor != null ? analysis.market_faktor : "—"}</span>
          <span className="num text-sm">—</span>
          <span />
        </div>
        )}
        {scenarios.map((s) => {
          const [labelKey, hintKey] = MRG_SCENARIO_I18N[s.key] || [];
          const cf = s.cf_mo;
          return (
            <div
              key={s.key}
              data-testid={`mrg-scenario-${s.key}`}
              data-current={s.current ? "true" : undefined}
              className={`${MRG_GRID} border-b border-border/60 last:border-b-0 ${s.current ? "bg-white/[0.04] border-l-2 border-l-white/60" : ""}`}
            >
              <div className="min-w-0">
                <div className="text-sm text-white flex items-center gap-2 flex-wrap">
                  {labelKey ? t(`listingDetail.${labelKey}`) : s.key}
                  {s.current && (
                    <span
                      className="border border-white/30 text-text-secondary px-1.5 py-px font-mono text-[9px] uppercase tracking-widest2"
                      data-testid="mrg-current-marker"
                    >
                      {t("listingDetail.mrgAnalysisCurrentMarker")}
                    </span>
                  )}
                </div>
                {hintKey && (
                  <div className="text-[10px] font-mono text-text-muted">{t(`listingDetail.${hintKey}`)}</div>
                )}
              </div>
              <span className="num text-sm text-white">
                {s.ceiling_m2 != null ? `€${s.ceiling_m2.toFixed(2)}/m²` : "—"}
              </span>
              <span className="num text-sm text-white">{s.faktor ?? "—"}</span>
              <span className={`num text-sm ${cf != null ? (cf >= 0 ? "text-signal-green" : "text-signal-red") : "text-text-muted"}`}>
                {cf != null ? `${cf >= 0 ? "+" : "−"}€${Math.abs(Math.round(cf))}` : "—"}
              </span>
              <span
                className={`border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest2 ${MRG_VERDICT_CHIP[s.verdict] || "border-border text-text-muted"}`}
                data-testid={`mrg-verdict-${s.key}`}
              >
                {MRG_VERDICT_I18N[s.verdict] ? t(`listingDetail.${MRG_VERDICT_I18N[s.verdict]}`) : s.verdict || "—"}
              </span>
            </div>
          );
        })}
      </div>

      {checklist.length > 0 && (
        <details className="mt-3 border border-border" data-testid="mrg-analysis-checklist">
          <summary className="cursor-pointer select-none px-3 py-2 overline">
            {baujahrUnknown
              ? t("listingDetail.mrgAnalysisChecklistSummaryUnknown")
              : t("listingDetail.mrgAnalysisChecklistSummary")}
          </summary>
          <div className="border-t border-border/60">
            {checklist.map((key) => (
              <div
                key={key}
                className="px-3 py-2 text-[11px] text-text-secondary leading-relaxed border-b border-border/60 last:border-b-0"
                data-testid={`mrg-checklist-${key}`}
              >
                {MRG_CHECKLIST_I18N[key] ? t(`listingDetail.${MRG_CHECKLIST_I18N[key]}`) : key}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function DocumentRow({ doc, status }) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-border/60 pt-2 text-[11px] font-mono">
      <div className="min-w-0">
        <div className="text-white truncate">{doc.document_type || "—"} · {doc.file_name || "—"}</div>
        <div className="text-text-muted">
          {doc.created_at ? new Date(doc.created_at).toLocaleString("de-AT") : "—"}
          {status ? ` · ${status}` : ""}
        </div>
      </div>
      <a
        href={`${BACKEND_URL}/api/export/document/${doc.document_id}.md`}
        target="_blank"
        rel="noreferrer"
        className="shrink-0 text-text-muted hover:text-white"
      >
        md
      </a>
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div className="bg-bg p-3">
      <div className="overline">{label}</div>
      <div className="num text-sm mt-1 text-white">{value}</div>
    </div>
  );
}

function FaktorBar({ faktor, grade, t }) {
  const thresholds = {
    A: { green: 20, border: 22, yellow: 26, max: 40 },
    "B+": { green: 19, border: 20.5, yellow: 23, max: 40 },
    B: { green: 17.8, border: 19.2, yellow: 22, max: 40 },
    "B-": { green: 16.7, border: 17.8, yellow: 20, max: 40 },
  };
  const th = thresholds[grade] || thresholds.A;
  const safeFaktor = faktor ?? 0;
  const pos = Math.min(100, (safeFaktor / th.max) * 100);
  const gn = (th.green / th.max) * 100;
  const gb = (th.border / th.max) * 100;
  const yl = (th.yellow / th.max) * 100;

  return (
    <div className="mt-3 relative">
      <div className="h-3 w-full bg-zinc-900 border border-border relative overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-signal-green/30" style={{ width: `${gn}%` }} />
        <div className="absolute inset-y-0 bg-signal-green/20" style={{ left: `${gn}%`, width: `${gb - gn}%` }} />
        <div className="absolute inset-y-0 bg-signal-yellow/25" style={{ left: `${gb}%`, width: `${yl - gb}%` }} />
        <div className="absolute inset-y-0 bg-signal-red/30" style={{ left: `${yl}%`, right: 0 }} />
      </div>
      <div
        className="absolute -top-1 w-0.5 h-5 bg-white"
        style={{ left: `calc(${pos}% - 1px)` }}
      />
      <div className="mt-2 flex justify-between text-[10px] font-mono text-text-muted">
        <span>0</span>
        <span className="text-signal-green">{th.green}</span>
        <span className="text-signal-yellow">{th.yellow}</span>
        <span className="text-signal-red">{th.max}+</span>
      </div>
      <div className="mt-3 text-[10px] font-mono text-text-secondary">
        {t("listingDetail.dealFaktorLabel", { faktor: safeFaktor.toFixed(1), grade, green: th.green })}
      </div>
    </div>
  );
}
